import React, { FunctionComponent, useEffect } from 'react';
import { useMeasure, useToggle } from 'react-use';
import { theme } from '@noths/polaris-client-styles';
import { CSSProp } from 'styled-components';

import chevronUp from 'src/assets/svg/chevronUp.svg';
import { IconButton } from 'src/components/atoms/IconButton';
import {
  ACCORDION__CONTENT_WRAPPER_CLASS,
  ACCORDION__HEADING_CLASS,
  ACCORDION__TITLE_CLASS,
} from '../constants/classNames';
import * as defaultStyles from '../styles';

export interface AccordionProps {
  title: string;
  id: string;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4';
  children?: React.ReactNode;
  styles?: CSSProp;
  isOpen?: boolean;
}

export const Accordion: FunctionComponent<AccordionProps> = ({
  id,
  title,
  headingLevel = 'h3',
  styles,
  children,
  ...props
}: AccordionProps) => {
  const isOpenProp = props.isOpen || false;
  const [isOpen, toggleOpen] = useToggle(isOpenProp);

  const buttonId = `accordion_${id}__button`;
  /*
    Note: changing accordion_copy-block__section could affect tracking
    https://notonthehighstreet.atlassian.net/browse/FFA-514?focusedCommentId=54133)
  */
  const sectionId = `accordion_${id}__section`;
  const Heading = headingLevel;
  const [contentRef, contentDimensions] = useMeasure<HTMLDivElement>();
  const contentHeight = contentDimensions.height;

  useEffect(
    () => {
      if (isOpenProp !== isOpen) {
        toggleOpen();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpenProp],
  );

  /*
    Note: removing aria-expanded could affect tracking
    https://notonthehighstreet.atlassian.net/browse/FFA-514?focusedCommentId=54133)
  */
  return (
    <div data-testid="accordion" css={[defaultStyles.accordion, styles]} data-is-open={isOpen}>
      <Heading className={ACCORDION__HEADING_CLASS}>
        <IconButton
          aria-expanded={isOpen}
          aria-controls={sectionId}
          id={buttonId}
          onClick={toggleOpen}
          icon={chevronUp}
          iconWidth={16}
          iconHeight={16}
        >
          <span className={ACCORDION__TITLE_CLASS}>{title}</span>
        </IconButton>
      </Heading>
      <div
        style={{
          height: isOpen ? (contentHeight ? `${contentHeight + theme.spacing.xl}px` : 'auto') : 0,
        }}
        className={ACCORDION__CONTENT_WRAPPER_CLASS}
        id={sectionId}
        role="region"
        aria-labelledby={buttonId}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
};
